import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Edit, Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Info, Download, Upload, GripVertical, Check, X, ArrowLeft, List as ListIcon, AlertTriangle, Minus } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import ModelWizard from './ModelWizard';
import ModelEditModal from './ModelEditModal';
import CSVImportWizard from './CSVImportWizard';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  default_for_tasks?: string[];
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  last_updated?: string;
  normalizedCost?: number;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  functionCalling?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}

interface ModelItem { modelId: string; rank: number; }
interface ModelList {
  id: string; name: string; description?: string; isActive: boolean;
  items: ModelItem[]; createdAt: string; updatedAt: string;
}

interface BuildingList { id?: string; name: string; desc: string; items: ModelItem[]; }

const MAX_LISTS = 10;

export default function Models() {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin || false;

  // ── Models state ──────────────────────────────────────────────────────────
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sortField, setSortField] = useState<string>('ranked_position');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [uniqueCapabilities, setUniqueCapabilities] = useState<string[]>([]);
  const [modelTypeFilter, setModelTypeFilter] = useState<'all' | 'global' | 'custom' | 'privacy'>('all');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: { row: number; message: string }[] } | null>(null);
  const [showCSVWizard, setShowCSVWizard] = useState(false);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── Tab + List state ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<string>('models');
  const [lists, setLists] = useState<ModelList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listSuccess, setListSuccess] = useState('');
  const [listError, setListError] = useState('');

  // ── Builder state ─────────────────────────────────────────────────────────
  const [builderSearch, setBuilderSearch] = useState('');
  const [buildingList, setBuildingList] = useState<BuildingList | null>(null);
  const [builderReturnTab, setBuilderReturnTab] = useState<string>('models');
  const [builderError, setBuilderError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────────
  const checkTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  const scrollTable = (dir: 'left' | 'right') => {
    tableScrollRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [globalResponse, customResponse] = await Promise.all([
        apiService.get('/portal/models/raw'),
        apiService.get('/portal/models/custom'),
      ]);
      const globalModels = Array.isArray(globalResponse.data) ? globalResponse.data : Array.isArray(globalResponse) ? globalResponse : [];
      const customModels = Array.isArray(customResponse.data) ? customResponse.data : Array.isArray(customResponse) ? customResponse : [];
      const allModels = [...globalModels, ...customModels];
      const capabilitySet = new Set<string>();
      allModels.forEach((model: AIModel) => { if (model.capabilities) Object.keys(model.capabilities).forEach(c => capabilitySet.add(c)); });
      setUniqueCapabilities(Array.from(capabilitySet).sort());
      setModels(allModels.sort((a: AIModel, b: AIModel) => (a.ranked_position || 999) - (b.ranked_position || 999)));
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLists = useCallback(async () => {
    try {
      setListsLoading(true);
      const res = await apiService.getModelLists();
      setLists((res as any).data || res);
    } catch (err: any) {
      setListError(err.message || 'Failed to load lists');
    } finally {
      setListsLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);
  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { setListSuccess(''); setListError(''); }, [activeTab]);

  useEffect(() => {
    const t = setTimeout(checkTableScroll, 100);
    return () => clearTimeout(t);
  }, [models, modelTypeFilter]);

  useEffect(() => {
    if (models.length === 0) return;
    setModels(prev => {
      const sorted = [...prev].sort((a, b) => {
        let aVal: any = sortField.startsWith('capability.') ? a.capabilities?.[sortField.replace('capability.', '')] || 0 : a[sortField as keyof AIModel];
        let bVal: any = sortField.startsWith('capability.') ? b.capabilities?.[sortField.replace('capability.', '')] || 0 : b[sortField as keyof AIModel];
        if (aVal === undefined || aVal === null) aVal = sortField === 'ranked_position' ? 999 : '';
        if (bVal === undefined || bVal === null) bVal = sortField === 'ranked_position' ? 999 : '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        return sortDirection === 'asc' ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
      });
      return sorted;
    });
  }, [sortField, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Model actions ─────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const handleEdit = (model: AIModel) => { setEditingModel(model); setIsCreating(false); setIsModalOpen(true); };
  const handleCreate = () => { setEditingModel(null); setIsCreating(true); setIsModalOpen(true); };

  const handleDelete = async (model: AIModel) => {
    const affectedLists = lists.filter(l => l.items.some(i => i.modelId === model.id));
    const listWarning = affectedLists.length > 0
      ? `\n\nThis model is in ${affectedLists.length} list${affectedLists.length !== 1 ? 's' : ''} (${affectedLists.map(l => `"${l.name}"`).join(', ')}) and will be removed from ${affectedLists.length !== 1 ? 'them' : 'it'} automatically.`
      : '';
    if (!window.confirm(`Delete model "${model.name}"?${listWarning}`)) return;
    try {
      await apiService.delete(`/portal/models/${encodeURIComponent(model.id)}`);
      await loadModels();
      if (affectedLists.length > 0) await loadLists();
    } catch (err: any) { setError(err.message || 'Failed to delete model'); }
  };

  const handleSaveModel = async (model: AIModel): Promise<string | null> => {
    try {
      if (isCreating) await apiService.post('/portal/models', model);
      else await apiService.put(`/portal/models/${encodeURIComponent(model.id)}`, model);
      setIsModalOpen(false);
      await loadModels();
      return null;
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to save model';
      setError(msg);
      alert(`Error: ${msg}`);
      return msg;
    }
  };

  const getApiBaseUrl = () => process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5001/api/v1');

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await fetch(`${getApiBaseUrl()}/portal/models/csv-template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'custom-models-template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message || 'Failed to download template'); }
  };

  const handleCSVImportComplete = async (imported: number, errors: { row: number; message: string }[]) => {
    setShowCSVWizard(false);
    setImportResult({ imported, errors });
    await loadModels();
  };

  const filteredModels = models.filter(m => {
    if (modelTypeFilter === 'global') return m.provider !== 'custom';
    if (modelTypeFilter === 'custom') return m.provider === 'custom';
    if (modelTypeFilter === 'privacy') return m.isPrivacyCompliant === true;
    return true;
  });

  // ── Builder actions ───────────────────────────────────────────────────────
  const openCreateBuilder = () => {
    setBuilderReturnTab(activeTab);
    setBuildingList({ name: '', desc: '', items: [] });
    setBuilderError('');
    setBuilderSearch('');
  };

  const openEditBuilder = (list: ModelList) => {
    setBuilderReturnTab(list.id);
    setBuilderSearch('');
    setBuildingList({
      id: list.id,
      name: list.name,
      desc: list.description || '',
      items: [...list.items].sort((a, b) => a.rank - b.rank),
    });
    setBuilderError('');
  };

  const closeBuilder = () => {
    setBuildingList(null);
    setBuilderError('');
    setSaving(false);
    setActiveTab(builderReturnTab);
  };

  const addToBuilding = (modelId: string) => {
    setBuildingList(prev => {
      if (!prev || prev.items.some(i => i.modelId === modelId)) return prev;
      return { ...prev, items: [...prev.items, { modelId, rank: prev.items.length + 1 }] };
    });
  };

  const removeFromBuilding = (modelId: string) => {
    setBuildingList(prev => {
      if (!prev) return prev;
      const next = prev.items.filter(i => i.modelId !== modelId);
      next.forEach((item, idx) => { item.rank = idx + 1; });
      return { ...prev, items: next };
    });
  };

  const handleDragStart = (idx: number) => setDragIndex(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx || !buildingList) return;
    setBuildingList(prev => {
      if (!prev) return prev;
      const next = [...prev.items];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      next.forEach((item, i) => { item.rank = i + 1; });
      return { ...prev, items: next };
    });
    setDragIndex(idx);
  };
  const handleDragEnd = () => setDragIndex(null);

  const handleSaveList = async () => {
    if (!buildingList) return;
    if (!buildingList.name.trim()) { setBuilderError('List name is required'); return; }
    if (buildingList.items.length === 0) { setBuilderError('Add at least one model'); return; }
    const hasTD = buildingList.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    if (!hasTD) { setBuilderError('At least one TD model is required'); return; }
    setSaving(true); setBuilderError('');
    try {
      if (buildingList.id) {
        await apiService.updateModelList(buildingList.id, {
          name: buildingList.name.trim(),
          description: buildingList.desc.trim() || undefined,
          items: buildingList.items,
        });
        setListSuccess('Model list updated');
        setBuildingList(null); setBuilderError(''); setSaving(false);
        await loadLists();
        setActiveTab(buildingList.id);
      } else {
        const created = await apiService.createModelList(buildingList.name.trim(), buildingList.desc.trim() || undefined, buildingList.items);
        setListSuccess('Model list created');
        setBuildingList(null); setBuilderError(''); setSaving(false);
        const fresh = await apiService.getModelLists();
        const freshLists: ModelList[] = (fresh as any).data || fresh;
        setLists(freshLists);
        // Navigate to the newly created list's tab
        const newList = freshLists.find(l => l.name === buildingList.name.trim());
        setActiveTab(newList?.id || 'models');
      }
    } catch (err: any) {
      setBuilderError(err.message || 'Failed to save list');
      setSaving(false);
    }
  };

  const handleDeleteList = async (listId: string, listName: string) => {
    if (!window.confirm(`Delete "${listName}"? This will remove it from any API keys that reference it.`)) return;
    try {
      await apiService.deleteModelList(listId);
      setListSuccess('Model list deleted');
      if (activeTab === listId) setActiveTab('models');
      await loadLists();
    } catch (err: any) { setListError(err.message || 'Failed to delete list'); }
  };

  // ── Shared table header ───────────────────────────────────────────────────
  const RANK_W = 80;   // px — must match left-0 + sticky left on Name
  const NAME_W = 200;  // px min-width for Name column

  const SortableHeader = ({ field, children, sticky }: { field: string; children: React.ReactNode; sticky?: 'rank' | 'name' }) => {
    const stickyStyle: React.CSSProperties = sticky === 'rank'
      ? { width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0, position: 'sticky', zIndex: 3, background: 'var(--arc-bg-2)' }
      : sticky === 'name'
      ? { minWidth: NAME_W, left: RANK_W, position: 'sticky', zIndex: 3, background: 'var(--arc-bg-2)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.08)' }
      : {};
    return (
      <th
        style={{ cursor: 'pointer', userSelect: 'none', ...stickyStyle }}
        className="sortable"
        onClick={() => handleSort(field)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{children}</span>
          {sortField === field && (sortDirection === 'asc' ? <ChevronUp size={14} className="pk-i" style={{ color: 'var(--arc-accent)' }} /> : <ChevronDown size={14} style={{ color: 'var(--arc-accent)' }} />)}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}>
        <div className="pk-skel" style={{ width: 32, height: 32, borderRadius: '50%' }} />
      </div>
    );
  }

  // ── Builder mode (full-page split) ────────────────────────────────────────
  if (buildingList !== null) {
    const hasTD = buildingList.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    const modelMap = new Map(models.map(m => [m.id, m]));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 6rem)' }}>
        {/* Builder top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingBottom: 16, flexShrink: 0, flexWrap: 'wrap' }}>
          <button type="button" onClick={closeBuilder} className="pk-btn-text" style={{ color: 'var(--arc-ink-2)' }}>
            <ArrowLeft size={16} /> Back to lists
          </button>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <input
              type="text"
              placeholder="List name *"
              value={buildingList.name}
              onChange={e => setBuildingList(p => p ? { ...p, name: e.target.value } : p)}
              className="pk-input"
              style={{ width: 208, minHeight: 34 }}
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={buildingList.desc}
              onChange={e => setBuildingList(p => p ? { ...p, desc: e.target.value } : p)}
              className="pk-input"
              style={{ width: 256, minHeight: 34 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {builderError && <span className="pk-badge pk-badge-danger">{builderError}</span>}
            {!hasTD && buildingList.items.length > 0 && !builderError && (
              <span className="pk-badge pk-badge-warning">Add a TD model</span>
            )}
            <button type="button" onClick={closeBuilder} className="pk-btn pk-btn-quiet">Cancel</button>
            <button
              type="button"
              onClick={handleSaveList}
              disabled={saving || !hasTD || buildingList.items.length === 0 || !buildingList.name.trim()}
              className="pk-btn pk-btn-ink"
            >
              {saving ? <span className="pk-spinner" style={{ width: 13, height: 13 }} /> : <Check size={14} />}
              {buildingList.id ? 'Save Changes' : 'Create List'}
            </button>
          </div>
        </div>

        {/* Split layout */}
        <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
          {/* Left: model table */}
          <div className="pk-card pk-card-flush" style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {/* Filter + search bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--arc-line-2)', background: 'var(--arc-bg-2)', flexShrink: 0, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search models..."
                value={builderSearch}
                onChange={e => setBuilderSearch(e.target.value)}
                className="pk-input pk-small"
                style={{ width: 160, minHeight: 30 }}
              />
              <div style={{ width: 1, height: 16, background: 'var(--arc-line)' }} />
              <div className="pk-seg">
                {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={modelTypeFilter === f}
                    className={modelTypeFilter === f ? 'is-active' : ''}
                    onClick={() => setModelTypeFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              <span className="pk-small" style={{ marginLeft: 'auto' }}>
                {(() => {
                  const q = builderSearch.toLowerCase();
                  return q ? filteredModels.filter(m => m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q)).length : filteredModels.length;
                })()} models · click row to add
              </span>
            </div>

            {/* Table */}
            <div style={{ overflow: 'auto', flex: 1 }}>
              <table className="pk-table" style={{ width: 'max-content', minWidth: '100%' }}>
                <thead>
                  <tr>
                    <SortableHeader field="ranked_position" sticky="rank">Rank</SortableHeader>
                    <SortableHeader field="name" sticky="name">Name</SortableHeader>
                    <SortableHeader field="provider">Provider</SortableHeader>
                    <SortableHeader field="providerModelId">Model ID</SortableHeader>
                    <SortableHeader field="category">Category</SortableHeader>
                    <SortableHeader field="price_per_token">Price</SortableHeader>
                    <SortableHeader field="contextWindow">Context</SortableHeader>
                    <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                    <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                    <SortableHeader field="taskDeterminationModel">TD</SortableHeader>
                    <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                    <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                    <SortableHeader field="supports_vision">Vision</SortableHeader>
                    <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                    {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                    <SortableHeader field="date_of_model">Date</SortableHeader>
                    <th style={{ position: 'sticky', right: 0, background: 'var(--arc-bg-2)' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModels
                    .filter(m => {
                      const q = builderSearch.toLowerCase();
                      return !q || m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q);
                    })
                    .map(model => {
                    const inList = buildingList.items.some(i => i.modelId === model.id);
                    const tick = (val: boolean | undefined) => val ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />;
                    const rowBg = inList ? 'var(--arc-accent-soft)' : 'var(--arc-paper)';
                    return (
                      <tr
                        key={model.id}
                        onClick={() => !inList && addToBuilding(model.id)}
                        style={{ background: inList ? rowBg : undefined, cursor: inList ? 'default' : 'pointer' }}
                      >
                        <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0, background: rowBg }} className="pk-td-sticky">{model.ranked_position || '—'}</td>
                        <td style={{ minWidth: NAME_W, left: RANK_W, background: rowBg, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.08)' }} className="pk-td-sticky strong">
                          {model.name}
                          {model.provider === 'custom' && <span className="pk-badge pk-badge-accent" style={{ marginLeft: 6 }}>Custom</span>}
                        </td>
                        <td>{model.provider}</td>
                        <td className="id" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{model.providerModelId || '—'}</td>
                        <td>{model.category || '—'}</td>
                        <td className="num">{model.price_per_token || '—'}</td>
                        <td className="num">{model.contextWindow ? `${(model.contextWindow / 1000).toFixed(0)}k` : '—'}</td>
                        <td className="num">{model.maxTokens ? `${(model.maxTokens / 1000).toFixed(0)}k` : '—'}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.supports_streaming)}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.taskDeterminationModel)}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.isPrivacyCompliant)}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.functionCalling)}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.supports_vision)}</td>
                        <td style={{ textAlign: 'center' }}>{tick(model.isUncensored)}</td>
                        {uniqueCapabilities.map(c => (
                          <td key={c} style={{ textAlign: 'center' }}>
                            <span className={`pk-badge ${model.capabilities?.[c] ? 'pk-badge-accent' : ''}`}>{model.capabilities?.[c] || '—'}</span>
                          </td>
                        ))}
                        <td>{model.date_of_model || '—'}</td>
                        <td className="pk-td-sticky" style={{ right: 0, background: rowBg, textAlign: 'center', width: 40 }}>
                          {inList
                            ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} />
                            : <Plus size={14} className="pk-cross" style={{ margin: '0 auto' }} />
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: list panel */}
          <div className="pk-card pk-card-flush" style={{ width: 288, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '2px solid var(--arc-accent-soft)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--arc-line-2)', background: 'var(--arc-accent-soft)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 className="pk-h4" style={{ color: 'var(--arc-accent)' }}>In list</h3>
                <span className="pk-small" style={{ color: 'var(--arc-accent)' }}>{buildingList.items.length} model{buildingList.items.length !== 1 ? 's' : ''}</span>
              </div>
              <p className="pk-small" style={{ color: 'var(--arc-accent)', marginTop: 2 }}>Drag to reorder · rank 1 = highest priority</p>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {buildingList.items.length === 0 ? (
                <p className="pk-small" style={{ textAlign: 'center', padding: '32px 0' }}>Click a model from the table to add it</p>
              ) : (
                buildingList.items.map((item, idx) => {
                  const model = modelMap.get(item.modelId);
                  return (
                    <div
                      key={item.modelId}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--arc-line-2)', borderRadius: 'var(--arc-r-sm)',
                        padding: '6px 8px', background: 'var(--arc-paper)', cursor: 'grab', userSelect: 'none',
                        opacity: dragIndex === idx ? 0.5 : 1, borderColor: dragIndex === idx ? 'var(--arc-accent)' : undefined,
                      }}
                    >
                      <GripVertical size={13} style={{ color: 'var(--arc-ink-4)', flexShrink: 0 }} />
                      <span className="pk-mono pk-small" style={{ width: 16, textAlign: 'right', flexShrink: 0 }}>{item.rank}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span className="pk-small" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--arc-ink-2)' }}>{model?.name || item.modelId}</span>
                        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                          {model?.taskDeterminationModel && <span className="pk-badge pk-badge-accent" style={{ height: 16, padding: '0 5px', fontSize: 9.5 }}>TD</span>}
                          {model?.isPrivacyCompliant && <span className="pk-badge pk-badge-success" style={{ height: 16, padding: '0 5px', fontSize: 9.5 }}>P</span>}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeFromBuilding(item.modelId)} className="pk-btn pk-btn-quiet pk-btn-icon" style={{ width: 24, height: 24, flexShrink: 0 }}>
                        <X size={13} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List health checks ────────────────────────────────────────────────────
  const getListWarnings = (list: ModelList) => {
    const hasTD = list.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    const hasPrivacyTD = list.items.some(i => {
      const m = models.find(mo => mo.id === i.modelId);
      return m?.taskDeterminationModel && m?.isPrivacyCompliant;
    });
    const warnings: string[] = [];
    if (!hasTD) warnings.push('no-td');
    if (!hasPrivacyTD) warnings.push('no-privacy-td');
    return warnings;
  };

  // ── Normal page ───────────────────────────────────────────────────────────
  return (
    <div>
      {/* Row 1: Title + tabs (full width, no competition) */}
      <div style={{ marginBottom: 20 }}>
        <h1 className="pk-h1" style={{ marginBottom: 14 }}>Models</h1>
        <div className="pk-tabs" style={{ overflowX: 'auto' }}>
          <button
            type="button"
            onClick={() => setActiveTab('models')}
            className={`pk-tab ${activeTab === 'models' ? 'is-active' : ''}`}
            style={{ background: 'none', border: 0, cursor: 'pointer', flexShrink: 0, padding: '10px 0' }}
          >
            All Models
          </button>
          {lists.map(list => {
            const warnings = getListWarnings(list);
            const hasIssue = warnings.length > 0;
            return (
              <button
                key={list.id}
                type="button"
                onClick={() => setActiveTab(list.id)}
                className={`pk-tab ${activeTab === list.id ? 'is-active' : ''}`}
                style={{ background: 'none', border: 0, cursor: 'pointer', flexShrink: 0, padding: '10px 0', display: 'flex', alignItems: 'center', gap: 6 }}
                title={list.name}
              >
                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{list.name}</span>
                {hasIssue && <AlertTriangle size={13} style={{ color: 'var(--arc-danger)', flexShrink: 0 }} />}
              </button>
            );
          })}
          {lists.length < MAX_LISTS && (
            <button
              type="button"
              onClick={openCreateBuilder}
              className="pk-tab"
              style={{ background: 'none', border: 0, cursor: 'pointer', flexShrink: 0, padding: '10px 0', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--arc-ink-4)' }}
            >
              <Plus size={14} /> Create List
            </button>
          )}
        </div>
      </div>


      {/* ── All Models tab ── */}
      {activeTab === 'models' && (
        <>
          {error && <div className="pk-note pk-note-danger" style={{ marginBottom: 16 }}>{error}</div>}
          {importResult && (
            <div className="pk-note pk-note-accent" style={{ marginBottom: 16, flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontWeight: 700, color: 'var(--arc-ink)', margin: 0 }}>
                  Imported {importResult.imported} model{importResult.imported !== 1 ? 's' : ''}.
                  {importResult.errors.length > 0 && ` ${importResult.errors.length} row${importResult.errors.length !== 1 ? 's' : ''} had errors.`}
                </p>
                <button type="button" onClick={() => setImportResult(null)} className="pk-btn-text">Dismiss</button>
              </div>
              {importResult.errors.length > 0 && (
                <ul style={{ marginTop: 8, paddingLeft: 18, color: 'var(--arc-danger)' }} className="pk-small">
                  {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Filters + actions row */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            <span className="pk-small" style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>Filter:</span>
            <div className="pk-seg">
              {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={modelTypeFilter === f}
                  className={modelTypeFilter === f ? 'is-active' : ''}
                  onClick={() => setModelTypeFilter(f)}
                >
                  {f === 'all' ? 'All' : f === 'global' ? 'Global' : f === 'custom' ? 'Custom' : 'Privacy'}
                </button>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowConfigModal(true)}
                className="pk-btn pk-btn-quiet pk-btn-icon"
                title="Model configuration requirements"
              >
                <Info size={16} />
              </button>
              <button type="button" onClick={handleDownloadTemplate} className="pk-btn pk-btn-outline">
                <Download size={16} /> Template
              </button>
              <button type="button" onClick={() => setShowCSVWizard(true)} className="pk-btn pk-btn-outline">
                <Upload size={16} /> Import CSV
              </button>
              <button type="button" onClick={handleCreate} className="pk-btn pk-btn-ink">
                <Plus size={16} /> Add Model
              </button>
            </div>
          </div>

          {/* No-lists nudge */}
          {lists.length === 0 && !listsLoading && (
            <div className="pk-note pk-note-accent" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
              <p className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>
                <span style={{ fontWeight: 700, color: 'var(--arc-ink)' }}>Tip:</span> Create a model list to restrict which models an API key can use — great for privacy, cost control, or custom routing.
              </p>
              <button type="button" onClick={openCreateBuilder} className="pk-btn-text" style={{ flexShrink: 0, marginLeft: 16 }}>
                Create your first list →
              </button>
            </div>
          )}

          {/* Table */}
          <div className="pk-table-wrap pk-table-grad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: '1px solid var(--arc-line-2)', background: 'var(--arc-bg-2)' }}>
              <span className="pk-small">
                Sorted by <span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>{sortField.startsWith('capability.') ? sortField.replace('capability.', '') : sortField.replace(/_/g, ' ')}</span> {sortDirection === 'asc' ? '↑' : '↓'} · {filteredModels.length} of {models.length} models
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span className="pk-small" style={{ marginRight: 4 }}>Scroll</span>
                <button type="button" onClick={() => scrollTable('left')} disabled={!canScrollLeft} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"><ChevronLeft size={16} /></button>
                <button type="button" onClick={() => scrollTable('right')} disabled={!canScrollRight} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"><ChevronRight size={16} /></button>
              </div>
            </div>
            <div ref={tableScrollRef} onScroll={checkTableScroll} className="pk-table-scroll" style={{ maxHeight: 800 }}>
              <table className="pk-table" style={{ position: 'relative' }}>
                <thead>
                  <tr>
                    <SortableHeader field="ranked_position" sticky="rank">Rank</SortableHeader>
                    <SortableHeader field="name" sticky="name">Name</SortableHeader>
                    <SortableHeader field="provider">Provider</SortableHeader>
                    <SortableHeader field="providerModelId">Model ID</SortableHeader>
                    <SortableHeader field="price_per_token">Price</SortableHeader>
                    <SortableHeader field="category">Category</SortableHeader>
                    <SortableHeader field="contextWindow">Context Window</SortableHeader>
                    <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                    <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                    {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                    <SortableHeader field="supports_vision">Vision</SortableHeader>
                    <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                    <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                    <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                    <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                    <SortableHeader field="date_of_model">Date</SortableHeader>
                    <th style={{ position: 'sticky', right: 0, zIndex: 3, background: 'var(--arc-bg-2)', boxShadow: '-2px 0 5px -2px rgba(0,0,0,0.1)' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModels.map(model => {
                    return (
                    <tr key={model.id} className="group">
                      <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }} className="pk-td-sticky strong">{model.ranked_position || 'N/A'}</td>
                      <td style={{ minWidth: NAME_W, left: RANK_W, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.08)' }} className="pk-td-sticky strong">
                        {model.name}
                        {model.provider === 'custom' && <span className="pk-badge pk-badge-accent" style={{ marginLeft: 8 }}>Custom</span>}
                      </td>
                      <td>{model.provider}</td>
                      <td className="id" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{model.providerModelId}</td>
                      <td className="num">{model.price_per_token || 'N/A'}</td>
                      <td>{model.category || 'N/A'}</td>
                      <td className="num">{model.contextWindow || 'N/A'}</td>
                      <td className="num">{model.maxTokens || 'N/A'}</td>
                      <td style={{ textAlign: 'center' }}>{model.supports_streaming ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      {uniqueCapabilities.map(c => (
                        <td key={c} style={{ textAlign: 'center' }}>
                          <span className={`pk-badge ${model.capabilities?.[c] ? 'pk-badge-accent' : ''}`}>{model.capabilities?.[c] || '—'}</span>
                        </td>
                      ))}
                      <td style={{ textAlign: 'center' }}>{model.supports_vision ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      <td style={{ textAlign: 'center' }}>{model.isUncensored ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      <td style={{ textAlign: 'center' }}>{model.isPrivacyCompliant ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      <td style={{ textAlign: 'center' }}>{model.functionCalling ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      <td style={{ textAlign: 'center' }}>{model.taskDeterminationModel ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      <td>{model.date_of_model || 'N/A'}</td>
                      <td className="pk-td-sticky" style={{ right: 0, boxShadow: '-2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {(isAdmin || model.isUserCustom) && <button type="button" onClick={() => handleEdit(model)} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"><Edit size={15} /></button>}
                          {(isAdmin || model.isUserCustom) && <button type="button" onClick={() => handleDelete(model)} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm" style={{ color: 'var(--arc-danger)' }}><Trash2 size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── List tab view ── */}
      {activeTab !== 'models' && (() => {
        const activeList = lists.find(l => l.id === activeTab);
        if (!activeList) return (
          <div className="text-center py-16 text-gray-400 text-sm">List not found.</div>
        );
        const rankMap = new Map(activeList.items.map(i => [i.modelId, i.rank]));
        const listModels = models
          .filter(m => {
            if (!rankMap.has(m.id)) return false;
            if (modelTypeFilter === 'global') return m.provider !== 'custom';
            if (modelTypeFilter === 'custom') return m.provider === 'custom';
            if (modelTypeFilter === 'privacy') return m.isPrivacyCompliant === true;
            return true;
          })
          .sort((a, b) => {
            // When user hasn't changed sort (or clicked List Rank), sort by list rank
            if (sortField === 'ranked_position') {
              const diff = (rankMap.get(a.id) ?? 999) - (rankMap.get(b.id) ?? 999);
              return sortDirection === 'asc' ? diff : -diff;
            }
            // Otherwise models array is already sorted by handleSort — preserve that order
            return 0;
          });

        return (
          <div>
            {listError && (
              <div className="pk-note pk-note-danger" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                {listError}<button type="button" onClick={() => setListError('')} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"><X size={14} /></button>
              </div>
            )}
            {listSuccess && (
              <div className="pk-note pk-note-accent" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                {listSuccess}<button type="button" onClick={() => setListSuccess('')} className="pk-btn pk-btn-quiet pk-btn-icon pk-btn-sm"><X size={14} /></button>
              </div>
            )}

            {/* Health warnings */}
            {(() => {
              const warnings = getListWarnings(activeList);
              const noTD = warnings.includes('no-td');
              const noPrivacyTD = warnings.includes('no-privacy-td');
              if (!noTD && !noPrivacyTD) return null;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {noTD && (
                    <div className="pk-note pk-note-danger">
                      <AlertTriangle size={16} />
                      <div>
                        <p style={{ fontWeight: 700, color: 'var(--arc-ink)', margin: 0 }}>No task determination model in this list</p>
                        <p className="pk-small" style={{ marginTop: 2 }}>Task determination will fail for any API key using this list. Add at least one TD-enabled model to resolve this.</p>
                      </div>
                    </div>
                  )}
                  {noPrivacyTD && (
                    <div className="pk-note pk-note-warning">
                      <AlertTriangle size={16} />
                      <div>
                        <p style={{ fontWeight: 700, color: 'var(--arc-ink)', margin: 0 }}>No privacy-compliant TD model in this list</p>
                        <p className="pk-small" style={{ marginTop: 2 }}>Without a model that is both TD-enabled and privacy-compliant, task determination for privacy-mode requests may fall back to a non-private model. Consider adding one to keep the full request chain compliant.</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* List subtitle */}
            {activeList.description && (
              <p className="pk-sub" style={{ marginTop: -8, marginBottom: 12 }}><span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>Description:</span> {activeList.description}</p>
            )}

            {/* Filters + actions row */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              <span className="pk-small" style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>Filter:</span>
              <div className="pk-seg">
                {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={modelTypeFilter === f}
                    className={modelTypeFilter === f ? 'is-active' : ''}
                    onClick={() => setModelTypeFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'global' ? 'Global' : f === 'custom' ? 'Custom' : 'Privacy'}
                  </button>
                ))}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="pk-small">{listModels.length} model{listModels.length !== 1 ? 's' : ''}</span>
                <button type="button" onClick={() => openEditBuilder(activeList)} className="pk-btn pk-btn-outline">
                  <Edit size={16} /> Edit List
                </button>
                <button type="button" onClick={() => handleDeleteList(activeList.id, activeList.name)} className="pk-btn pk-btn-danger">
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>

            <div className="pk-table-wrap pk-table-grad">
              <div className="pk-table-scroll" style={{ maxHeight: 800 }}>
                <table className="pk-table" style={{ position: 'relative' }}>
                  <thead>
                    <tr>
                      <SortableHeader field="ranked_position" sticky="rank"><span style={{ color: 'var(--arc-accent)' }}>List Rank</span></SortableHeader>
                      <SortableHeader field="name" sticky="name">Name</SortableHeader>
                      <SortableHeader field="provider">Provider</SortableHeader>
                      <SortableHeader field="providerModelId">Model ID</SortableHeader>
                      <SortableHeader field="price_per_token">Price</SortableHeader>
                      <SortableHeader field="category">Category</SortableHeader>
                      <SortableHeader field="contextWindow">Context Window</SortableHeader>
                      <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                      <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                      {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                      <SortableHeader field="supports_vision">Vision</SortableHeader>
                      <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                      <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                      <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                      <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {listModels.map(model => (
                      <tr key={model.id} className="group">
                        <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }} className="pk-td-sticky" >
                          <span style={{ fontWeight: 700, color: 'var(--arc-accent)' }}>{rankMap.get(model.id)}</span>
                        </td>
                        <td style={{ minWidth: NAME_W, left: RANK_W, boxShadow: '2px 0 5px -2px rgba(0,0,0,0.08)' }} className="pk-td-sticky strong">
                          {model.name}
                          {model.provider === 'custom' && <span className="pk-badge pk-badge-accent" style={{ marginLeft: 8 }}>Custom</span>}
                        </td>
                        <td>{model.provider}</td>
                        <td className="id" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{model.providerModelId}</td>
                        <td className="num">{model.price_per_token || 'N/A'}</td>
                        <td>{model.category || 'N/A'}</td>
                        <td className="num">{model.contextWindow || 'N/A'}</td>
                        <td className="num">{model.maxTokens || 'N/A'}</td>
                        <td style={{ textAlign: 'center' }}>{model.supports_streaming ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                        {uniqueCapabilities.map(c => (
                          <td key={c} style={{ textAlign: 'center' }}>
                            <span className={`pk-badge ${model.capabilities?.[c] ? 'pk-badge-accent' : ''}`}>{model.capabilities?.[c] || '—'}</span>
                          </td>
                        ))}
                        <td style={{ textAlign: 'center' }}>{model.supports_vision ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                        <td style={{ textAlign: 'center' }}>{model.isUncensored ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                        <td style={{ textAlign: 'center' }}>{model.isPrivacyCompliant ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                        <td style={{ textAlign: 'center' }}>{model.functionCalling ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                        <td style={{ textAlign: 'center' }}>{model.taskDeterminationModel ? <Check size={14} className="pk-tick" style={{ margin: '0 auto' }} /> : <Minus size={14} className="pk-cross" style={{ margin: '0 auto' }} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Model modals */}
      {isCreating ? (
        <ModelWizard isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveModel} isAdmin={isAdmin} existingModels={models} />
      ) : (
        <ModelEditModal model={editingModel} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveModel} isAdmin={isAdmin} existingModels={models} />
      )}
      <CSVImportWizard isOpen={showCSVWizard} onClose={() => setShowCSVWizard(false)} onImportComplete={handleCSVImportComplete} existingModels={models} isAdmin={isAdmin} />

      {/* Config requirements modal */}
      {showConfigModal && (
        <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999, padding: 16 }} onClick={() => setShowConfigModal(false)}>
          <div className="pk-modal" style={{ maxWidth: 560, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
              <h3 className="pk-h3">Model Configuration Requirements</h3>
              <button type="button" onClick={() => setShowConfigModal(false)} className="pk-btn pk-btn-quiet pk-btn-icon"><X size={18} /></button>
            </div>
            <div className="pk-modal-body pk-prose" style={{ gap: 20 }}>
              <div>
                <h4 className="pk-h4" style={{ marginBottom: 4 }}>Task Determination (TD) Models</h4>
                <p>At least one model with <strong>Task Determination</strong> enabled must be included in every model list. TD models are used to classify incoming requests before routing them to the appropriate model.</p>
              </div>
              <div>
                <h4 className="pk-h4" style={{ marginBottom: 4 }}>Privacy-Compliant Models</h4>
                <p>If an API key uses <strong>Privacy-Compliant mode</strong>, at least one privacy-compliant model must exist in the list. Requests will fail if no privacy model is available.</p>
                <p style={{ marginTop: 8 }}>For best results, include a model that is <strong>both Privacy-Compliant and TD-enabled</strong>. When the router performs task determination, it will use a privacy-safe model if one is available — keeping the entire request chain compliant. Without one, task determination may fall back to a non-privacy model even if the final response model is compliant.</p>
              </div>
              <div>
                <h4 className="pk-h4" style={{ marginBottom: 4 }}>Custom Endpoint Models</h4>
                <p>Custom models require a valid <strong>endpoint URL</strong> and optionally a custom API key. These are only available when the API key's model selection mode is set to <em>All</em> or <em>Custom Only</em>.</p>
              </div>
              <div>
                <h4 className="pk-h4" style={{ marginBottom: 4 }}>Model List Limits</h4>
                <p>You can create up to <strong>{MAX_LISTS} model lists</strong>. Each list must contain at least one model and one TD model.</p>
              </div>
              <div>
                <h4 className="pk-h4" style={{ marginBottom: 4 }}>List Rank</h4>
                <p>The order of models in a list determines their <strong>priority</strong>. Rank 1 is the highest priority and will be selected first when the API key prioritises by rank. When prioritising by price, the cheapest eligible model is selected regardless of rank.</p>
              </div>
            </div>
            <div className="pk-modal-foot">
              <div className="spacer" />
              <button type="button" onClick={() => setShowConfigModal(false)} className="pk-btn pk-btn-ink">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
